import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, PartyPopper, Briefcase, Globe2, GraduationCap, Gift, MessageCircle, ShieldCheck, Video } from "lucide-react";
import { toast } from "sonner";
import { createEnrollment, confirmPayment, claimPayment } from "../lib/api";
import { RazorpayButton } from "./RazorpayButton";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const inputCls =
  "w-full rounded-xl border border-white/15 bg-void/60 px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-600 outline-none focus:border-gold/60 transition-colors";
const chipCls = "inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.03] px-3.5 py-1.5 font-sans text-xs text-neutral-300";

export const CourseModal = ({ course, mode, razorpayKey, onClose, onQuiz, onCounselling }) => {
  const [step, setStep] = useState(mode === "enroll" ? "enroll" : "details");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 10;
  const price = `₹${course.price.toLocaleString("en-IN")}`;

  const proceed = async () => {
    setBusy(true);
    try {
      const { enrollment_id, order } = await createEnrollment({ course_id: course.id, ...form });
      if (order.mode === "live") {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("checkout");
        await new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: razorpayKey,
            amount: order.amount,
            currency: order.currency,
            name: "Sanvalio Academy",
            description: course.title,
            order_id: order.id,
            prefill: { name: form.name, email: form.email, contact: /^\d{10}$/.test(form.phone.trim()) ? `+91${form.phone.trim()}` : form.phone.trim() },
            theme: { color: "#D4AF37" },
            handler: async (res) => {
              setConfirmation(await confirmPayment(enrollment_id, {
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              }));
              resolve();
            },
            modal: { ondismiss: () => reject(new Error("dismissed")) },
          });
          rzp.open();
        });
        toast.success("Admission confirmed — welcome to Sanvalio Academy!");
      } else if (order.mode === "razorpay_button") {
        setPending({ enrollment_id, buttonId: order.payment_button_id });
        setStep("pay");
      } else {
        setConfirmation(await claimPayment(enrollment_id));
        toast.success("Seat reserved — we'll call you shortly!");
      }
    } catch (e) {
      if (e?.message !== "dismissed") toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    setBusy(true);
    try {
      setConfirmation(await claimPayment(pending.enrollment_id));
      toast.success("Thank you! We're verifying your payment.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const waLink = (c) =>
    `https://wa.me/917850911096?text=${encodeURIComponent(
      `Hi Sanvalio Academy! I'm ${form.name}. I just enrolled in "${course.title}" (Enrollment ID: ${c.enrollment_id.slice(0, 8).toUpperCase()}). Please share my batch details & Google Meet link.`
    )}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-[16px] p-0 sm:p-6"
        onClick={onClose}
        data-testid="course-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 bg-elevated p-6 sm:p-10"
          data-testid="course-modal"
        >
          <button
            data-testid="course-modal-close"
            onClick={onClose}
            className="absolute right-5 top-5 rounded-full border border-white/15 p-2 text-neutral-400 hover:text-champagne hover:border-gold/50 transition-colors"
          >
            <X size={16} />
          </button>

          {confirmation ? (
            <div className="py-8 text-center" data-testid="enrollment-confirmation">
              <div className="relative mx-auto h-20 w-20">
                {[...Array(12)].map((_, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 1, x: "-50%", y: "-50%", scale: 0 }}
                    animate={{
                      opacity: 0,
                      x: `calc(-50% + ${Math.cos((i * 30 * Math.PI) / 180) * 95}px)`,
                      y: `calc(-50% + ${Math.sin((i * 30 * Math.PI) / 180) * 95}px)`,
                      scale: 1,
                    }}
                    transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
                    className={`absolute left-1/2 top-1/2 h-2 w-2 rounded-full ${i % 2 ? "bg-gold" : "bg-champagne"}`}
                  />
                ))}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.1 }}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-leaf/15 border border-leaf/50"
                >
                  <PartyPopper size={32} className="text-gold" />
                </motion.div>
              </div>
              <h3 className="mt-7 font-display text-3xl sm:text-4xl text-neutral-100" data-testid="confirmation-title">
                {confirmation.status === "admission_confirmed" ? "Admission Confirmed!" : confirmation.status === "payment_claimed" ? "Thank You — You're In!" : "Seat Reserved!"}
              </h3>
              <p className="mx-auto mt-4 max-w-md text-sm sm:text-base text-neutral-400 leading-relaxed">{confirmation.message}</p>
              <div className="mx-auto mt-7 max-w-sm rounded-2xl border border-gold/25 bg-gold/5 p-5 text-left text-sm">
                <p className="text-neutral-300"><span className="text-neutral-500">Course:</span> {course.title}</p>
                <p className="mt-1.5 text-neutral-300"><span className="text-neutral-500">Enrollment ID:</span> <span className="font-mono text-xs text-champagne">{confirmation.enrollment_id.slice(0, 8).toUpperCase()}</span></p>
                <p className="mt-1.5 text-neutral-300"><span className="text-neutral-500">Mode:</span> Live on Google Meet · {course.duration}</p>
              </div>
              <p className="mt-6 text-xs text-neutral-500">
                An email from Sanvalio Academy is on its way to <span className="text-champagne">{form.email}</span>
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a
                  data-testid="confirmation-whatsapp-button"
                  href={waLink(confirmation)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3 text-sm font-semibold text-void hover:bg-[#3ee07a] transition-colors"
                >
                  <MessageCircle size={16} /> Get Batch Details on WhatsApp
                </a>
                <button
                  data-testid="confirmation-done-button"
                  onClick={onClose}
                  className="rounded-full border border-gold/50 px-8 py-3 text-sm font-semibold text-champagne hover:bg-gold/10 transition-colors"
                >
                  Done — See You in Class
                </button>
              </div>
            </div>
          ) : step === "details" ? (
            <div data-testid="course-details-view">
              <div className="flex flex-wrap gap-2">
                <span className={`${chipCls} border-gold/30 bg-gold/10 text-champagne`}><GraduationCap size={13} /> {course.type}</span>
                <span className={chipCls}>{course.duration}</span>
                <span className={chipCls}><Video size={13} className="text-gold" /> Live on Google Meet</span>
              </div>
              <h3 className="mt-5 font-display text-3xl sm:text-4xl leading-tight text-neutral-100">{course.title}</h3>
              <p className="mt-2 font-sans text-sm text-gold/90">{course.tag}</p>
              {course.highlights && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="course-highlights">
                  {course.highlights.map((h) => (
                    <span key={h} className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 bg-gold/5 px-3 py-1.5 font-sans text-xs text-champagne/90"><Check size={12} className="text-gold" /> {h}</span>
                  ))}
                </div>
              )}

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: Briefcase, label: "Metro city jobs", value: course.salary },
                  { icon: Globe2, label: "Freelance earning", value: course.freelance },
                  { icon: Gift, label: "Bonus", value: "Free freelancing training" },
                ].map((s) => (
                  <div key={s.label} className="rounded-2xl border border-white/10 bg-void/50 p-5" data-testid={`outcome-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <s.icon size={18} className="text-gold" />
                    <p className="mt-3 font-sans text-xs text-neutral-500">{s.label}</p>
                    <p className="mt-1.5 font-display text-lg text-champagne">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <p className="font-sans text-xs font-medium uppercase tracking-[0.15em] text-gold/80">What you'll master</p>
                  <ul className="mt-4 space-y-3">
                    {course.benefits.map((b) => (
                      <li key={b} className="flex gap-3 text-sm text-neutral-300 leading-relaxed"><Check size={15} className="mt-0.5 shrink-0 text-gold" /> {b}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-sans text-xs font-medium uppercase tracking-[0.15em] text-gold/80">Doors this opens</p>
                  <ul className="mt-4 space-y-3">
                    {course.careers.map((c) => (
                      <li key={c} className="flex gap-3 text-sm text-neutral-300 leading-relaxed"><Check size={15} className="mt-0.5 shrink-0 text-gold" /> {c}</li>
                    ))}
                  </ul>
                  <p className="mt-5 font-sans text-xs font-medium uppercase tracking-[0.15em] text-gold/80">Future designations</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {course.designations.map((d) => (
                      <span key={d} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-neutral-300">{d}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gold/25 bg-gold/5 p-6">
                <div>
                  <p className="font-display text-3xl text-champagne">{price}</p>
                  <p className="mt-1 text-xs text-neutral-500">One-time · full program · {course.type.toLowerCase()} included</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button data-testid="details-enroll-button" onClick={() => setStep("enroll")} className="rounded-full bg-leaf px-7 py-3 text-sm font-semibold text-void hover:bg-champagne transition-colors">
                    Enroll Now
                  </button>
                  <button data-testid="details-quiz-button" onClick={onQuiz} className="rounded-full border border-white/20 px-6 py-3 text-sm text-neutral-200 hover:border-gold/60 hover:text-champagne transition-colors">
                    Still unsure? Free Assessment
                  </button>
                </div>
              </div>
            </div>
          ) : step === "pay" ? (
            <div data-testid="payment-step-view" className="text-center">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.15em] text-gold/80">Step 2 of 2 · Secure payment</p>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl text-neutral-100">{course.title}</h3>
              <p className="mt-2 text-sm text-neutral-400">Hi {form.name.split(" ")[0]}, complete your payment of <span className="font-display text-lg text-champagne">{price}</span> to lock your seat.</p>

              <div className="mx-auto mt-8 max-w-md rounded-2xl border border-gold/30 bg-gold/5 p-7">
                <RazorpayButton buttonId={pending.buttonId} />
                <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-neutral-500"><ShieldCheck size={13} className="text-gold" /> UPI · Cards · Netbanking · Secured by Razorpay</p>
              </div>

              <p className="mt-8 text-sm text-neutral-400">Payment done? Tell us so we can confirm your admission.</p>
              <button
                data-testid="payment-done-button"
                disabled={busy}
                onClick={claim}
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/60 px-7 py-3 text-sm font-semibold text-champagne hover:bg-gold/10 transition-colors disabled:opacity-40"
              >
                {busy && <Loader2 size={15} className="animate-spin" />} I've Completed Payment
              </button>
              <p className="mt-4 text-xs text-neutral-600">Enrollment ID: <span className="font-mono text-champagne">{pending.enrollment_id.slice(0, 8).toUpperCase()}</span></p>
            </div>
          ) : (
            <div data-testid="enroll-form-view">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.15em] text-gold/80">Enrollment · {course.type}</p>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl text-neutral-100">{course.title}</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className={chipCls}>{course.duration}</span>
                <span className={chipCls}><Video size={13} className="text-gold" /> Live on Google Meet</span>
                <span className={chipCls}><Gift size={13} className="text-gold" /> Free freelancing training</span>
              </div>

              <p className="mt-8 font-sans text-xs text-neutral-500">Your details — we'll call you on this number for batch confirmation</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="enroll-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
                <input data-testid="enroll-phone-input" className={inputCls} placeholder="Phone (WhatsApp)" value={form.phone} onChange={set("phone")} />
                <input data-testid="enroll-email-input" className={`${inputCls} sm:col-span-2`} placeholder="Email address" type="email" value={form.email} onChange={set("email")} />
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
                <div>
                  <p className="font-display text-2xl text-champagne">{price}</p>
                  <p className="mt-0.5 text-xs text-neutral-500">Secure payment via Razorpay · UPI, cards & netbanking</p>
                </div>
                <div className="flex gap-3">
                  <button data-testid="enroll-back-button" onClick={() => setStep("details")} className="rounded-full border border-white/20 px-6 py-3 text-sm text-neutral-300 hover:border-gold/50 transition-colors">
                    Back
                  </button>
                  <button
                    data-testid="enroll-pay-button"
                    disabled={!valid || busy}
                    onClick={proceed}
                    className="inline-flex items-center gap-2 rounded-full bg-leaf px-7 py-3 text-sm font-semibold text-void transition-all hover:bg-champagne disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {busy && <Loader2 size={15} className="animate-spin" />}
                    {busy ? "Processing…" : `Proceed to Pay ${price}`}
                  </button>
                </div>
              </div>
              <p className="mt-4 text-xs text-neutral-600">
                Prefer to talk first?{" "}
                <button data-testid="enroll-counselling-link" onClick={onCounselling} className="text-champagne underline underline-offset-4 hover:text-gold">
                  Book a free live counselling call
                </button>
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
